import { EmptyState, MainCard, PageHeader, usePageHeader } from '@easydoctor/ui';
import {
  QUEUE_PAGE_SIZE_DEFAULT,
  TICKET_LIST_SCOPE_LABELS,
  otherTicketListScope,
  type TicketListScope,
} from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { IconArchive, IconInbox } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLinkBase, useLocation, useMatch, useNavigate, useSearchParams } from 'react-router';
import { useTeamMembers } from '../../api/team';
import { countsCoverWholeQueue, useQueueTotals, useTicketQueue } from '../../api/tickets';
import { HelpButton } from '../../guida/HelpButton';
import { TicketDetailDrawer } from '../ticket';
import { QueueFilters } from './QueueFilters';
import { QueuePagination } from './QueuePagination';
import { QueueTable } from './QueueTable';
import { QueueViews } from './QueueViews';
import {
  DEFAULT_QUEUE_PARAMS,
  SCOPE_PATHS,
  crossScopeSearch,
  hasActiveFilters,
  hasFiltersOrSearch,
  queueHref,
  queuePath,
  readQueueParams,
  writeQueueParams,
  type QueueParams,
} from './queue-params';
import { useRowFocusReturn } from './use-row-focus-return';

/** Fuori dallo schermo ma letto dai lettori di schermo (come `visuallyHidden` di MUI). */
const VISUALLY_HIDDEN = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
} as const;

/** «1 ticket» o «N ticket». */
const ticketCount = (count: number) => (count === 1 ? '1 ticket' : `${count} ticket`);

/** Testi che cambiano fra coda e archivio (#123). */
interface ScopeTexts {
  /** Sottotitolo su tutto l'ambito, qualunque siano vista, filtri e ricerca. */
  total: (count: number) => string;
  /** «N risultati in coda/archivio»: il link della ricerca incrociata che porta a questo ambito dall'altro. */
  results: (count: number) => string;
  /** Nessun ticket nell'ambito, senza filtri né ricerca. */
  empty: string;
  loading: string;
  unavailable: string;
  back: string;
  /** Da «Cerca e filtra …» e «Ticket in …». */
  inScope: string;
  helpScreen: '/' | '/archivio';
}

const SCOPE_TEXTS: Record<TicketListScope, ScopeTexts> = {
  coda: {
    total: (count) => `${ticketCount(count)} apert${count === 1 ? 'o' : 'i'}`,
    results: (count) => `${count} risultat${count === 1 ? 'o' : 'i'} in coda`,
    empty: 'Nessun ticket aperto: la coda si riempie quando i prodotti collegati inviano le segnalazioni.',
    loading: 'Caricamento della coda…',
    unavailable: 'Coda non disponibile',
    back: 'Torna alla coda',
    inScope: 'la coda',
    helpScreen: '/',
  },
  archivio: {
    total: (count) => `${ticketCount(count)} in archivio`,
    results: (count) => `${count} risultat${count === 1 ? 'o' : 'i'} in archivio`,
    empty: 'Nessun ticket in archivio: qui arrivano i ticket risolti, chiusi e scartati.',
    loading: "Caricamento dell'archivio…",
    unavailable: 'Archivio non disponibile',
    back: "Torna all'archivio",
    inScope: "l'archivio",
    helpScreen: '/archivio',
  },
};

/** Perché la lista è vuota: ricerca, filtri o nessun ticket nell'ambito. */
function emptyText(params: QueueParams, scope: TicketListScope): string {
  if (params.q) return 'Nessun ticket corrisponde alla ricerca.';
  if (hasActiveFilters(params)) return 'Nessun ticket con questi filtri.';
  return SCOPE_TEXTS[scope].empty;
}

/** Pattern della rotta del dettaglio sopra ciascun ambito. */
const DETAIL_ROUTES: Record<TicketListScope, string> = { coda: '/ticket/:code', archivio: '/archivio/ticket/:code' };

/**
 * Coda (PAGE-001, PRD-004 US-1) e Archivio (#123): la stessa pagina, composta come la pagina Pazienti
 * di EasyCare (#105), su due ambiti. La coda, home della console, contiene il lavoro da fare (Nuovo,
 * In lavorazione, In attesa dell'utente); l'archivio i ticket Risolto, Chiuso e Scartato. Testata con
 * «N ticket aperti» o «N ticket in archivio» e il «?» della Guida; sotto, l'eventuale errore e una
 * `MainCard` con la barra di ricerca e filtri, le viste rapide dell'ambito, la ricerca incrociata
 * («N risultati in archivio», e viceversa), la `DataTable` e la paginazione. Vista, ordinamento,
 * filtri, ricerca e pagina stanno nell'URL; l'ambito è il percorso.
 *
 * Il dettaglio (`/ticket/:code` o `/archivio/ticket/:code`, PAGE-002) è una rotta figlia resa nel
 * `TicketDetailDrawer`, un pannello modale sopra la lista (#106) con «Torna alla coda» o «Torna
 * all'archivio»: la lista resta montata e piena sotto, quindi aprire e chiudere un ticket non ne perde
 * filtri, ordinamento né scorrimento. «Ticket N di M» conta le righe della pagina corrente.
 */
export function TicketListPage({ scope }: { scope: TicketListScope }) {
  const label = TICKET_LIST_SCOPE_LABELS[scope];
  const texts = SCOPE_TEXTS[scope];
  const other = otherTicketListScope(scope);
  const titleId = `${scope}-title`;
  // Il dettaglio aperto sopra dichiara il proprio titolo, che prevale finché è montato.
  usePageHeader({ title: label });
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => readQueueParams(searchParams, scope), [searchParams, scope]);
  const location = useLocation();
  const navigate = useNavigate();
  const selectedCode = useMatch(DETAIL_ROUTES[scope])?.params.code;

  const query = { scope, ...params, pageSize: QUEUE_PAGE_SIZE_DEFAULT };
  const queue = useTicketQueue(query);
  const team = useTeamMembers();
  // Senza filtri né ricerca la lista stessa scrive i contatori di tutto l'ambito: nessuna richiesta in più.
  const totals = useQueueTotals(scope, { enabled: !countsCoverWholeQueue(query) });

  // Ogni cambio di vista o filtro torna alla prima pagina; la storia non si riempie di filtri.
  const update = useCallback(
    (changes: Partial<QueueParams>) =>
      setSearchParams((current) => writeQueueParams({ ...readQueueParams(current, scope), page: 1, ...changes }), {
        replace: true,
      }),
    [setSearchParams, scope]
  );
  // Rimontare i filtri azzera anche la ricerca ancora in attesa di partire. L'ordinamento non è un
  // filtro: «Azzera filtri» lo conserva.
  const [filtersKey, setFiltersKey] = useState(0);
  const clear = useCallback(() => {
    setFiltersKey((key) => key + 1);
    setSearchParams((current) =>
      writeQueueParams({ ...DEFAULT_QUEUE_PARAMS, sort: readQueueParams(current, scope).sort })
    );
  }, [setSearchParams, scope]);
  // Chiudere sostituisce la voce del ticket: «Indietro» non lo riapre.
  const closeDetail = () => void navigate(queuePath(location.search, scope), { replace: true });
  // Alla chiusura del dettaglio (anche con «Indietro») il focus torna alla riga del ticket.
  const tableRef = useRowFocusReturn(selectedCode);

  const data = queue.data;
  const filtersActive = hasActiveFilters(params);
  const pages = data ? Math.ceil(data.total / data.pageSize) : 0;
  // Pagina oltre l'ultima (link vecchio, ticket diminuiti): si torna all'ultima che esiste.
  const lastPage = Math.max(pages, 1);
  const pageOutOfRange = Boolean(data && !queue.isPlaceholderData && params.page > lastPage);
  useEffect(() => {
    if (pageOutOfRange) update({ page: lastPage });
  }, [pageOutOfRange, lastPage, update]);

  // Ricerca incrociata (#123): con filtri o ricerca, quanti risultati ha l'altro ambito, con un link.
  const elsewhere = data && hasFiltersOrSearch(params) ? data.otherScopeTotal : 0;
  const elsewhereText = elsewhere > 0 ? SCOPE_TEXTS[other].results(elsewhere) : '';
  // Il lettore di schermo sente anche i risultati dall'altra parte, che compaiono sotto le viste.
  const status = queue.isPending
    ? texts.loading
    : data
      ? [ticketCount(data.total), elsewhereText].filter(Boolean).join(', ')
      : '';
  // «Ticket N di M»: la posizione fra le righe della pagina che si vede sotto il pannello.
  const pageCodes = useMemo(() => data?.items.map((item) => item.code) ?? [], [data]);

  return (
    <>
      <Stack component="section" aria-labelledby={titleId} spacing={3}>
        <PageHeader
          title={label}
          titleId={titleId}
          titleAdornment={<HelpButton screen={texts.helpScreen} />}
          description={totals.data === undefined ? undefined : texts.total(totals.data)}
        />

        {queue.isError && !data && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void queue.refetch()} disabled={queue.isFetching}>
                Riprova
              </Button>
            }
          >
            <AlertTitle>{texts.unavailable}</AlertTitle>
            {queue.error.message}
          </Alert>
        )}

        <MainCard>
          <Stack spacing={3}>
            <QueueFilters
              key={filtersKey}
              scope={scope}
              params={params}
              committenti={data?.committenti ?? []}
              members={team.data?.members ?? []}
              onChange={update}
              onClear={filtersActive ? clear : undefined}
            />
            <QueueViews
              scope={scope}
              active={params.view}
              counts={data?.counts}
              onSelect={(view) => update({ view })}
            />
            {/* Quanti ticket ci sono, per chi usa un lettore di schermo: cambia con vista, filtri e ricerca. */}
            <Box role="status" sx={VISUALLY_HIDDEN}>
              {status}
            </Box>
            {elsewhere > 0 && (
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ alignItems: 'center', color: 'text.secondary' }}
                data-testid="altro-ambito"
              >
                {other === 'archivio' ? <IconArchive size={18} aria-hidden /> : <IconInbox size={18} aria-hidden />}
                <Link
                  component={RouterLinkBase}
                  to={{ pathname: SCOPE_PATHS[other], search: crossScopeSearch(params) }}
                  variant="body2"
                  sx={{ fontWeight: 500 }}
                >
                  {elsewhereText}
                </Link>
              </Stack>
            )}

            {(queue.isPending || (data && data.items.length > 0 && !pageOutOfRange)) && (
              <Box ref={tableRef} aria-busy={queue.isPending} sx={{ position: 'relative' }}>
                {queue.isPlaceholderData && (
                  // Esplicito: il tema di `@easydoctor/ui` mette `determinate` come predefinito, che senza `value`
                  // lascia la barra vuota.
                  <LinearProgress
                    variant="indeterminate"
                    aria-label="Aggiornamento della coda"
                    sx={{ position: 'absolute', inset: '0 0 auto', zIndex: 1 }}
                  />
                )}
                <QueueTable
                  scope={scope}
                  items={data?.items ?? []}
                  loading={queue.isPending}
                  currentCode={selectedCode}
                  search={location.search}
                />
              </Box>
            )}
            {data && data.items.length === 0 && !pageOutOfRange && (
              <EmptyState
                variant="plain"
                icon={<IconInbox />}
                title={emptyText(params, scope)}
                action={
                  filtersActive && (
                    <Button variant="outlined" color="inherit" onClick={clear}>
                      Azzera filtri
                    </Button>
                  )
                }
                sx={{ py: 7 }}
              />
            )}
            {data && data.items.length > 0 && !pageOutOfRange && (
              <QueuePagination
                total={data.total}
                page={params.page}
                pageSize={data.pageSize}
                onPageChange={(page) => update({ page })}
              />
            )}
          </Stack>
        </MainCard>
      </Stack>

      {selectedCode && (
        <TicketDetailDrawer
          code={selectedCode}
          codes={pageCodes}
          onClose={closeDetail}
          backLabel={texts.back}
          crumb={{ label, href: queueHref(location.search, scope) }}
        />
      )}
    </>
  );
}
